import { CloseOutlined } from "@ant-design/icons";
import { CSSProperties, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TEXT_COLOR } from "../../lib/style_definitions";
import { ModalActionContext } from "../../lib/hooks/useModalAction";
import { MIconButton } from "./miconButton";

interface MModalProps {
  visible: boolean;
  content: React.ReactNode;
  onClose(): void;
  footer?: React.ReactNode;
  title?: string;
  style?: CSSProperties;
  className?: string;
  hideCloseIcon?: boolean;
}

export function MModal(props: MModalProps) {
  const { visible, onClose } = props;
  const pendingActions = useRef(new Set<symbol>());
  const [isBusy, setIsBusy] = useState(false);

  const setActionPending = useCallback((actionId: symbol, pending: boolean) => {
    if (pendingActions.current.has(actionId) === pending) {
      return;
    }

    if (pending) {
      pendingActions.current.add(actionId);
    } else {
      pendingActions.current.delete(actionId);
    }

    setIsBusy(pendingActions.current.size > 0);
  }, []);

  function handleClose() {
    if (pendingActions.current.size > 0) {
      return;
    }

    onClose();
  }

  if (!visible) {
    return null;
  }

  return createPortal(
    <ModalActionContext.Provider value={{ isBusy, setActionPending }}>
      <div className="mask" onClick={handleClose} />
      <div className={"mmodal-cover " + props.className} onClick={handleClose}>
        <div
          className={"mmodal"}
          aria-busy={isBusy}
          style={props.style}
          onClick={(e) => e.stopPropagation()}
        >
          {(props.title || !props.hideCloseIcon) && (
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                minHeight: 24,
                paddingRight: props.hideCloseIcon ? 0 : 40,
                marginBottom: "20px",
              }}
            >
              {props.title && (
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: TEXT_COLOR,
                    overflowWrap: "anywhere",
                  }}
                >
                  {props.title}
                </div>
              )}
              {!props.hideCloseIcon && (
                <MIconButton
                  icon={<CloseOutlined />}
                  title="Luk"
                  onClick={handleClose}
                  color="#9ca3af"
                  background="transparent"
                  style={{
                    position: "absolute",
                    top: -4,
                    right: 0,
                    width: 32,
                    height: 32,
                    fontSize: 16,
                    borderRadius: 8,
                  }}
                />
              )}
            </div>
          )}
          {props.content}
          {props.footer && (
            <div style={{ marginTop: "24px" }}>{props.footer}</div>
          )}
        </div>
      </div>
    </ModalActionContext.Provider>,
    document.body,
  );
}
